import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { createBranch } from "../../services/branch";
import {
  BranchConflictError,
  BranchErrorResponse,
} from "../../types/branch.type";
import BranchFormFields from "./BranchFormFields";
import {
  CREATE_FIELDS,
  EMPTY_BRANCH_FORM,
  buildBranchCreatePayload,
  composeBranchCode,
  composeSpecialRouteCode,
} from "./branchForm";
import { resolveBranchSaveFailure } from "./branchStatus";
import { CREATE_FAILED_MESSAGE, branchDetailPath } from "./constants";
import { useBranchForm } from "./useBranchForm";

interface CreateBranchDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The one form a new branch needs (จังหวะ 1). The server composes the code, allocates the
 * company id, mirrors the catalogue and creates the special route in the same request; on
 * success the user lands on the new branch's readiness checklist.
 */
export default function CreateBranchDialog({ open, onClose }: CreateBranchDialogProps) {
  const navigate = useNavigate();
  const { values, errors, setErrors, handleFieldChange, reset, validate } =
    useBranchForm(EMPTY_BRANCH_FORM);
  const [saving, setSaving] = useState<boolean>(false);
  const [failureMessage, setFailureMessage] = useState<string>("");

  useEffect(() => {
    if (open) {
      reset(EMPTY_BRANCH_FORM);
      setFailureMessage("");
    }
  }, [open, reset]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSubmit = async () => {
    if (!validate(CREATE_FIELDS)) return;
    setSaving(true);
    setFailureMessage("");
    try {
      const {
        data: { data },
      } = await createBranch(buildBranchCreatePayload(values));
      if (data) navigate(branchDetailPath(data.uuid));
      else onClose();
    } catch (error) {
      const response = isAxiosError<BranchErrorResponse<BranchConflictError>>(error)
        ? error.response
        : undefined;
      const failure = resolveBranchSaveFailure(
        response?.status,
        response?.data,
        CREATE_FAILED_MESSAGE,
      );
      setFailureMessage(failure.message);
      setErrors(failure.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  const branchCode = composeBranchCode(values.branch_number, values.branch_prefix);
  const specialRouteCode = composeSpecialRouteCode(values.branch_prefix);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>สร้างสาขาใหม่</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <BranchFormFields
            values={values}
            errors={errors}
            onFieldChange={handleFieldChange}
          />
          <Stack direction="row" alignItems="baseline" gap={1}>
            <Typography variant="body2" color="text.secondary">
              รหัสสาขาที่จะได้
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {branchCode}
            </Typography>
          </Stack>
          <Alert severity="info">
            ระบบจะสร้างให้อัตโนมัติในคำขอเดียวกัน: สาย {specialRouteCode} (สายพิเศษ)
            พร้อมแผนวิ่ง 7 วัน และสินค้า/สมุดราคา (catalogue) ของสาขา · เลขสาขาและตัวย่อแก้ไม่ได้หลังสร้าง
          </Alert>
          {failureMessage && <Alert severity="error">{failureMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={handleClose}>
          ยกเลิก
        </Button>
        <Button variant="contained" loading={saving} onClick={handleSubmit}>
          สร้างสาขา
        </Button>
      </DialogActions>
    </Dialog>
  );
}
